import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input'
import 'react-phone-number-input/style.css'

// =============================================================================
// PhoneField
//
// Thin wrapper around react-phone-number-input that matches the visual
// language of the existing <Field> component (label + hint + 14px input
// with the accent focus ring). Country selector + libphonenumber-backed
// validation + E.164 normalization come for free.
//
// `value` is the E.164 representation (e.g. `+13035551234`) — empty
// string when no number is entered. `onChange` fires with the E.164
// string or `undefined`; we normalize to '' for the parent so it can
// treat the field as a plain string everywhere else.
//
// Default country is US. Users can pick another from the flag dropdown.
// =============================================================================

export interface PhoneFieldProps {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
  required?: boolean
  hint?: string
  /** Default country in ISO 3166-1 alpha-2 (uppercase). Defaults to 'US'. */
  defaultCountry?: string
}

export function PhoneField({
  id,
  label,
  value,
  onChange,
  required = false,
  hint,
  defaultCountry = 'US',
}: PhoneFieldProps) {
  const valid = !value || isValidPhoneNumber(value)
  return (
    <label htmlFor={id} className="flex flex-col gap-1.5">
      <span className="flex items-baseline justify-between gap-2">
        <span className="text-[12px] font-medium uppercase tracking-[0.08em] text-[var(--color-muted)]">
          {label}
        </span>
        {hint ? (
          <span className="text-[11px] font-normal normal-case text-[var(--color-muted)]/80">
            {hint}
          </span>
        ) : null}
      </span>
      <div
        className={[
          'gb-phone-wrap',
          'w-full px-3 py-2.5 rounded-[var(--radius-DEFAULT)]',
          'bg-[var(--color-surface)] border',
          value && !valid ? 'border-[var(--color-signal)]' : 'border-[var(--color-border)]',
          'text-[14px] text-[var(--color-text)]',
          'focus-within:border-[var(--color-accent)] focus-within:ring-2 focus-within:ring-[var(--color-accent)]/30',
          'transition-colors',
        ].join(' ')}
      >
        <PhoneInput
          id={id}
          international
          defaultCountry={defaultCountry as 'US'}
          value={value || undefined}
          onChange={(next) => onChange((next ?? '') as string)}
          // Country select + flag stays in-line on the left of the input.
          // The wrapping div carries our focus ring; the input itself is
          // borderless / transparent so it inherits the wrapper.
        />
      </div>
      {value && !valid ? (
        <span className="text-[11px] text-[var(--color-signal)]">
          Doesn&apos;t look like a valid phone number.
        </span>
      ) : null}
      {/* Hidden mirror lets us hook into native required validation if
          required=true and a parent submits via plain <form>. */}
      {required ? (
        <input
          type="hidden"
          name={`${id}-validity`}
          value={valid && value ? value : ''}
          required
          aria-hidden
        />
      ) : null}
    </label>
  )
}

